import { type PayloadAction } from '@reduxjs/toolkit';

import { ConceptGroupSelection } from '@verantos-dev/types-concepts';

import type { CohortBuilderSlice } from '../types';

/**
 * Set concept group selections (unified structure with domain context)
 *
 * The backend sends ALL selections (including old ones with reported=true),
 * so we simply replace the entire array. Filtering of reported selections
 * happens in the selector (selectConceptGroupSelectionsWithIssues).
 */
export const setConceptGroupSelectionsReducer = (
  state: CohortBuilderSlice,
  action: PayloadAction<{
    conceptGroupSelections: ConceptGroupSelection[];
  }>
): CohortBuilderSlice => {
  return {
    ...state,
    conceptGroupSelections: action.payload.conceptGroupSelections,
  };
};

/**
 * Optimistically update concept group selection reported status
 * Used when user clicks suggested action to create concept group
 * This optimistic update will be synced from backend after creation response or on reload
 */
export const optimisticCgsReportedUpdateReducer = (
  state: CohortBuilderSlice,
  action: PayloadAction<{
    filterKey: string;
  }>
): CohortBuilderSlice => {
  if (!state.conceptGroupSelections) {
    return state;
  }

  return {
    ...state,
    conceptGroupSelections: state.conceptGroupSelections.map((selection) =>
      selection.filterKey === action.payload.filterKey
        ? { ...selection, reported: true }
        : selection
    ),
  };
};

/**
 * Optimistically mark multiple concept group selections as reported/reviewed
 * Used when user saves a container (subject variable/event) or makes a follow-up request
 * Supports marking by filterKey array, containerName, or all selections
 */
export const markConceptGroupSelectionsAsReportedReducer = (
  state: CohortBuilderSlice,
  action: PayloadAction<{
    filterKeys?: string[];
    containerName?: string;
    markAll?: boolean;
  }>
): CohortBuilderSlice => {
  if (!state.conceptGroupSelections) {
    return state;
  }

  const { filterKeys, containerName, markAll } = action.payload;

  // If filterKeys provided, mark those specific selections
  if (filterKeys && filterKeys.length > 0) {
    return {
      ...state,
      conceptGroupSelections: state.conceptGroupSelections.map((selection) =>
        filterKeys.includes(selection.filterKey) ? { ...selection, reported: true } : selection
      ),
    };
  }

  // If markAll is true, mark all selections (except error cases without valid concept groups)
  if (markAll) {
    return {
      ...state,
      conceptGroupSelections: state.conceptGroupSelections.map((selection) => {
        // Exception: Don't mark as reported if status is error and no valid concept groups
        const isErrorWithoutGroups =
          selection.status === 'error' &&
          (!selection.selectedConceptGroups || selection.selectedConceptGroups.length === 0);

        if (isErrorWithoutGroups) {
          return selection; // Keep unreported
        }

        return { ...selection, reported: true };
      }),
    };
  }

  // If containerName provided, mark selections matching that container
  if (containerName) {
    const normalizedContainerName = containerName.toLowerCase();
    return {
      ...state,
      conceptGroupSelections: state.conceptGroupSelections.map((selection) =>
        selection.containerName?.toLowerCase() === normalizedContainerName
          ? { ...selection, reported: true }
          : selection
      ),
    };
  }

  // If neither provided, return state unchanged
  return state;
};
